import { ChangeDetectionStrategy, Component, HostListener, inject, signal } from '@angular/core';
import { TuiButton, TuiIcon, TuiDialogService } from '@taiga-ui/core';
import { TuiAvatar, TuiBadge } from '@taiga-ui/kit';
import { tuiDialog } from '@taiga-ui/core';
import { AuthService } from '../../services/auth.service';
import { PermissionsService } from '../../../../core/auth/permissions.service';
import { LoginDialogComponent } from '../login-dialog/login-dialog.component';
import { RegisterDialogComponent } from '../register-dialog/register-dialog.component';
import { NavigationHelperService } from '../../../../shared/services/navigation-helper.service';
import { MyInvitationsStore } from '../../../my-team/services/my-invitations-store.service';

@Component({
  selector: 'app-auth-buttons',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TuiButton, TuiAvatar, TuiIcon, TuiBadge],
  template: `
    @if (!authService.isAuthenticated()) {
      <div class="auth-buttons">
        <button
          type="button"
          tuiButton
          appearance="flat"
          size="s"
          class="auth-buttons__register"
          (click)="openRegisterDialog()"
          aria-label="Sign Up"
          title="Sign Up"
        >
          <tui-icon class="auth-buttons__icon" icon="@tui.user-plus"></tui-icon>
          <span class="auth-buttons__text">Sign Up</span>
        </button>

        <button
          type="button"
          tuiButton
          appearance="flat"
          size="s"
          class="auth-buttons__login"
          (click)="openLoginDialog()"
          aria-label="Sign In"
          title="Sign In"
        >
          <tui-icon class="auth-buttons__icon" icon="@tui.user"></tui-icon>
          <span class="auth-buttons__text">Sign In</span>
        </button>
      </div>
    } @else {
      <div class="auth-buttons auth-buttons--logged-in">
        <div class="auth-buttons__dropdown-container">
          <button
            type="button"
            class="auth-buttons__user-menu"
            (click)="toggleDropdown($event)"
            [class.active]="dropdownOpen()"
            aria-haspopup="true"
            [attr.aria-expanded]="dropdownOpen()"
          >
            <span class="auth-buttons__avatar-wrap">
              <div tuiAvatar
                size="s"
                class="auth-buttons__avatar"
              >
                @if (userAvatar()) {
                  <img alt="{{ authService.currentUser()?.username }}" [src]="userAvatar()" />
                }
              </div>
              @if (invitationsStore.pendingCount() > 0) {
                <span
                  tuiBadge
                  appearance="primary"
                  size="s"
                  class="auth-buttons__invite-badge"
                  [attr.aria-label]="invitationsStore.pendingCount() + ' pending invitations'"
                  title="Pending team invitations"
                >{{ invitationsStore.pendingCount() }}</span>
              }
            </span>
            <span class="auth-buttons__username">{{ authService.currentUser()?.username }}</span>
            <tui-icon class="auth-buttons__dropdown-icon" icon="@tui.chevron-down"></tui-icon>
          </button>

          @if (dropdownOpen()) {
            <div class="auth-buttons__dropdown-menu">
              <button
                type="button"
                class="auth-buttons__dropdown-item"
                (click)="goToProfile()"
              >
                <tui-icon icon="@tui.user"></tui-icon>
                <span>Profile</span>
              </button>
              @if (invitationsStore.pendingCount() > 0) {
                <button
                  type="button"
                  class="auth-buttons__dropdown-item auth-buttons__dropdown-item--invite"
                  (click)="goToInvitations()"
                >
                  <tui-icon icon="@tui.mail"></tui-icon>
                  <span>Invitations</span>
                  <span tuiBadge appearance="error" size="s" class="auth-buttons__item-badge">{{ invitationsStore.pendingCount() }}</span>
                </button>
              }
              @if (isAdmin()) {
                <button
                  type="button"
                  class="auth-buttons__dropdown-item"
                  (click)="goToSettings()"
                >
                  <tui-icon icon="@tui.settings"></tui-icon>
                  <span>Settings</span>
                </button>
              }
              <button
                type="button"
                class="auth-buttons__dropdown-item auth-buttons__dropdown-item--logout"
                (click)="logout()"
              >
                <tui-icon icon="@tui.log-out"></tui-icon>
                <span>Logout</span>
              </button>
            </div>
          }
        </div>
      </div>
    }
  `,
  styleUrl: './auth-buttons.component.less',
})
export class AuthButtonsComponent {
  protected readonly authService = inject(AuthService);
  private readonly permissions = inject(PermissionsService);
  protected readonly invitationsStore = inject(MyInvitationsStore);
  private readonly navigationHelper = inject(NavigationHelperService);
  private readonly dialogs = inject(TuiDialogService);

  // Non-dismissible: the Cap widget resizes on click, so an overlay/Esc
  // dismiss can fire mid-interaction and drop the form. Close via the X.
  private readonly loginDialog = tuiDialog(LoginDialogComponent, {
    size: 'm',
    dismissible: false,
    label: 'Sign In',
  });

  private readonly registerDialog = tuiDialog(RegisterDialogComponent, {
    size: 'm',
    dismissible: false,
    label: 'Create Account',
  });

  readonly dropdownOpen = signal(false);
  readonly isAdmin = this.permissions.isAdmin;

  @HostListener('document:click', ['$event.target'])
  onClickOutside(target: EventTarget | null): void {
    if (!this.dropdownOpen() || !target) {
      return;
    }

    const targetElement = target as HTMLElement;
    const authButtonsElement = document.querySelector('app-auth-buttons');
    if (authButtonsElement && !authButtonsElement.contains(targetElement)) {
      this.dropdownOpen.set(false);
    }
  }

  toggleDropdown(event: Event): void {
    event.stopPropagation();
    this.dropdownOpen.update(open => !open);
  }

  closeDropdown(): void {
    this.dropdownOpen.set(false);
  }

  protected userAvatar(): string {
    const user = this.authService.currentUser();
    return user?.person_id ? `/api/persons/${user.person_id}/photo` : '';
  }

  openLoginDialog(): void {
    this.loginDialog().subscribe();
  }

  openRegisterDialog(): void {
    this.registerDialog().subscribe();
  }

  goToProfile(): void {
    this.closeDropdown();
    const user = this.authService.currentUser();
    if (user?.id) {
      this.navigationHelper.toUserProfile(user.id, false);
    } else {
      this.navigationHelper.toHome();
    }
  }

  goToInvitations(): void {
    this.closeDropdown();
    const user = this.authService.currentUser();
    if (user?.id) {
      this.navigationHelper.toUserProfile(user.id, false);
    } else {
      this.navigationHelper.toHome();
    }
  }

  goToSettings(): void {
    this.closeDropdown();
    this.navigationHelper.toSettings();
  }

  logout(): void {
    this.closeDropdown();
    this.authService.logout();
    this.navigationHelper.toHome();
  }
}
